import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useSiteContextStore } from '../lib/siteContextStore.js';

import { DomainNotConfiguredPage } from './DomainNotConfiguredPage.js';

/**
 * Wraps the router and serves the right tenant when the SPA loads on a custom
 * domain. It resolves site-context once, then:
 *   • canonical / app  → render the app/router unchanged (host only adds branding)
 *   • storefront       → redirect the bare path into the canonical /s/:slug tree,
 *                        so every existing storefront route + link keeps working
 *   • unconfigured     → a friendly "domain not configured" page
 * On the platform host (or a reserved path, or with the feature off) this is a
 * pass-through, so existing behaviour is byte-for-byte unchanged.
 */
export function SiteContextGate({ children }: { children: ReactNode }): JSX.Element {
  const status = useSiteContextStore((s) => s.status);
  const tenant = useSiteContextStore((s) => s.tenant);
  const resolve = useSiteContextStore((s) => s.resolve);
  const location = useLocation();

  useEffect(() => {
    void resolve();
  }, [resolve]);

  if (status === 'idle' || status === 'resolving') {
    return (
      <div className="grid min-h-screen place-items-center bg-bg text-fg-muted">
        <div
          className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand"
          aria-hidden
        />
      </div>
    );
  }

  if (status === 'unconfigured') return <DomainNotConfiguredPage />;

  // THE SHOP'S FRONT DOOR on a shared domain. duhokstationery.com serves the
  // dashboard under /admin and the shop under /web, so /web is folded into the
  // canonical /s/:slug tree — the same move this gate already makes for a
  // storefront-only host, keyed on the path instead of the hostname. Every
  // storefront route and link keeps working with no re-basing.
  if (tenant && (location.pathname === '/web' || location.pathname.startsWith('/web/'))) {
    const rest = location.pathname.slice('/web'.length);
    return <Navigate replace to={`/s/${tenant.slug}${rest}${location.search}`} />;
  }

  if (status === 'storefront' && tenant && !location.pathname.startsWith('/s/')) {
    const rest = location.pathname === '/' ? '' : location.pathname;
    return <Navigate replace to={`/s/${tenant.slug}${rest}${location.search}`} />;
  }

  return <>{children}</>;
}
